import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, Trash2, Upload, Download, Loader2, File as FileIcon } from "lucide-react";

const DOC_TYPES = [
  { value: "501c3", label: "501(c)(3) Letter" },
  { value: "budget", label: "Budget" },
  { value: "narrative", label: "Narrative" },
  { value: "logic_model", label: "Logic Model" },
  { value: "letters_of_support", label: "Letter of Support" },
  { value: "financials", label: "Financials" },
  { value: "other", label: "Other" },
];

const BLOB_URL_TTL_MS = 60_000;

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [textOpen, setTextOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [textForm, setTextForm] = useState({ name: "", doc_type: "narrative", content: "" });
  const [uploadForm, setUploadForm] = useState({ name: "", doc_type: "other", file: null });

  const load = () => api.get("/documents").then((r) => setDocs(r.data.documents || []));
  useEffect(() => { load(); }, []);

  const createText = async () => {
    if (!textForm.name.trim()) { toast.error("Name required"); return; }
    await api.post("/documents", textForm);
    toast.success("Document saved");
    setTextForm({ name: "", doc_type: "narrative", content: "" });
    setTextOpen(false);
    load();
  };

  const uploadFile = async () => {
    if (!uploadForm.file) { toast.error("Choose a file first"); return; }
    if (!uploadForm.name.trim()) { toast.error("Name required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("name", uploadForm.name);
      fd.append("doc_type", uploadForm.doc_type);
      const token = localStorage.getItem("gf_token");
      const res = await fetch(`${API_BASE}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("File uploaded");
      setUploadForm({ name: "", doc_type: "other", file: null });
      setUploadOpen(false);
      load();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openFile = async (doc) => {
    const token = localStorage.getItem("gf_token");
    const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Could not open file"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Vault</div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">Documents</h1>
          <p className="text-[#5E5E5E] mt-2">Upload files or write notes. Reuse across every application.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="docs-upload-open" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"><Upload className="w-4 h-4 mr-2"/>Upload file</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white" data-testid="upload-doc-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Upload a file</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={uploadForm.name} onChange={(e)=>setUploadForm({...uploadForm, name: e.target.value})} data-testid="upload-name" className="mt-2 bg-white border-[#E6E4DF]" placeholder="Bridge Trust 501c3 Determination"/>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={uploadForm.doc_type} onValueChange={(v)=>setUploadForm({...uploadForm, doc_type: v})}>
                    <SelectTrigger data-testid="upload-type" className="mt-2 bg-white border-[#E6E4DF]"><SelectValue/></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d)=><SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>File (PDF, DOCX, images, TXT — up to 10 MB)</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,.csv,.json"
                    onChange={(e)=>setUploadForm({...uploadForm, file: e.target.files?.[0] || null})}
                    data-testid="upload-file"
                    className="mt-2 bg-white border-[#E6E4DF] cursor-pointer file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-[#E8ECEB] file:text-[#1C3F35] file:text-xs file:font-medium"
                  />
                  {uploadForm.file && <div className="text-xs text-[#5E5E5E] mt-2">{uploadForm.file.name} · {(uploadForm.file.size / 1024).toFixed(0)} KB</div>}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={()=>setUploadOpen(false)} data-testid="upload-cancel">Cancel</Button>
                  <Button onClick={uploadFile} disabled={uploading} data-testid="upload-submit" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Upload"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={textOpen} onOpenChange={setTextOpen}>
            <DialogTrigger asChild>
              <Button data-testid="docs-new" variant="outline" className="border-[#E6E4DF]"><Plus className="w-4 h-4 mr-2"/>Add note</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white" data-testid="new-doc-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl">New document</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={textForm.name} onChange={(e)=>setTextForm({...textForm, name: e.target.value})} data-testid="doc-name" className="mt-2 bg-white border-[#E6E4DF]"/>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={textForm.doc_type} onValueChange={(v)=>setTextForm({...textForm, doc_type: v})}>
                    <SelectTrigger data-testid="doc-type" className="mt-2 bg-white border-[#E6E4DF]"><SelectValue/></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d)=><SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Content</Label>
                  <Textarea rows={8} value={textForm.content} onChange={(e)=>setTextForm({...textForm, content: e.target.value})} data-testid="doc-content" className="mt-2 bg-white border-[#E6E4DF] font-mono text-sm" placeholder="Paste content, notes, or a reference URL."/>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={()=>setTextOpen(false)} data-testid="doc-cancel">Cancel</Button>
                  <Button onClick={createText} data-testid="doc-save" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">Save</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {docs.length === 0 ? (
        <div className="text-center py-24 text-[#5E5E5E] bg-white border border-[#E6E4DF] rounded-lg">
          No documents yet. Upload your 501(c)(3), budget, and logic model to reuse across applications.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="docs-list">
          {docs.map((d) => (
            <li key={d.id} className="bg-white border border-[#E6E4DF] rounded-lg p-6 group" data-testid={`doc-${d.id}`}>
              <div className="flex items-start justify-between">
                {d.storage_path ? (
                  <FileIcon className="w-6 h-6 text-[#C1654B]"/>
                ) : (
                  <FileText className="w-6 h-6 text-[#1C3F35]"/>
                )}
                <div className="flex items-center gap-1">
                  {d.storage_path && (
                    <button onClick={()=>openFile(d)} className="text-[#5E5E5E] hover:text-[#1C3F35] p-1" data-testid={`doc-download-${d.id}`} title="Open">
                      <Download className="w-4 h-4"/>
                    </button>
                  )}
                  <button onClick={()=>remove(d.id)} className="text-[#5E5E5E] hover:text-[#C1654B] p-1" data-testid={`doc-delete-${d.id}`} title="Delete">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              </div>
              <div className="mt-4 font-medium text-[#1A1A1A] truncate">{d.name}</div>
              <div className="text-xs text-[#5E5E5E] mt-1 capitalize">
                {d.doc_type.replace(/_/g, " ")}
                {d.storage_path && d.size_kb ? ` · ${d.size_kb} KB` : ""}
              </div>
              {d.content && !d.storage_path && (
                <div className="mt-3 text-xs text-[#5E5E5E] line-clamp-3 whitespace-pre-wrap">{d.content}</div>
              )}
              {d.original_filename && (
                <div className="mt-3 text-xs text-[#5E5E5E] truncate font-mono">{d.original_filename}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
